import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal, Input } from '@/components/ui';
import { Order, OPERATION_TYPES } from '@/types';
import { useUserStore } from '@/store/userStore';
import { getFileTypeFromOperation } from '@/utils/constants';
import { apiService } from '@/api';
import { CREDIT_COSTS, CREDIT_COST } from '@/api/config';
import { calculateExtendCredits, calculateEditCredits, isExtendableFalModel } from '@/utils/modelConfigs';
import { calculateHeygenCredits } from '@/utils/constants';
import { Button } from '@/components/ui';
import { 
  Download, Share2, Music, Crown, Lock, Sparkles, Loader2, ChevronsRight, Pencil, Play, Clock
} from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  trackVideoDownloaded,
  trackVideoShared,
  trackVideoRemixStarted,
  trackFullscreenBlocked,
  trackVideoPreviewReady,
  trackPaywallCtaClicked,
  trackUpgradeBannerClicked,
} from '@/services/analyticsService';

interface MediaDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
}

export function MediaDetailModal({ isOpen, onClose, order }: MediaDetailModalProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userId, isPro, hasEnoughCredits, decrementCredits } = useUserStore();
  const isProUser = isPro();
  
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [recreateInput, setRecreateInput] = useState('');
  const [isRecreating, setIsRecreating] = useState(false);
  const [recreateError, setRecreateError] = useState<string | null>(null);
  
  // Extend video state (Veo 3.1 orders and their Gemini Omni Flash / H3 Max replacements)
  const [extendInput, setExtendInput] = useState('');
  const [isExtending, setIsExtending] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);
  
  // Edit video state (for Wan videos)
  const [editWanInput, setEditWanInput] = useState('');
  const [isEditingWan, setIsEditingWan] = useState(false);
  const [editWanError, setEditWanError] = useState<string | null>(null);

  // Edit video state (for HeyGen/Auto videos)
  const [heygenEditInput, setHeygenEditInput] = useState('');
  const [heygenEditStep, setHeygenEditStep] = useState<'prompt' | 'duration'>('prompt');
  const [heygenEditDuration, setHeygenEditDuration] = useState(60);
  const [isEditingHeygen, setIsEditingHeygen] = useState(false);
  const [heygenEditError, setHeygenEditError] = useState<string | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Preview order fake play state
  const [isPlayingPreview, setIsPlayingPreview] = useState(false);
  const [previewPlayProgress, setPreviewPlayProgress] = useState(0);
  const [previewReady, setPreviewReady] = useState(false);
  
  // Check if this is a preview order
  const isPreviewOrder = order?.orderStatus === 'preview' || order?.isPreview;
  
  // Credit costs - calculated dynamically based on model and duration
  // Extend: ~7s on H3 Max extend
  const extendCreditCost = calculateExtendCredits();
  // Edit/Remix: Based on the model used (Sora 2, Sora 2 Pro, or Grok)
  const remixCreditCost = calculateEditCredits(order?.falModel);
  // Wan edit: Keep fixed cost (Wan not in MODEL_CONFIGS)
  const editWanCreditCost = CREDIT_COSTS.video_edit_wan || CREDIT_COST;
  
  // Determine which action UI to show based on the model used.
  // Both 'heygen' (classic auto mode) and 'heygen_v3_chat' (v2 plan-first flow)
  // produce HeyGen videos — the edit-under-video path re-uploads the output
  // URL via uploadHeygenAssetByUrl and kicks off a standard heygen order, so
  // either source works identically here.
  const isHeygenVideo =
    order?.videoProvider === 'heygen' || order?.videoProvider === 'heygen_v3_chat';
  const isVeo31Video = !isHeygenVideo && isExtendableFalModel(order?.falModel);
  const isSora2Video = !isHeygenVideo && order?.falModel?.includes('sora-2');
  const isGrokVideo = !isHeygenVideo && order?.falModel?.includes('grok-imagine');
  const isWanVideo = !isHeygenVideo && order?.falModel?.includes('wan');
  const supportsEdit = isSora2Video || isGrokVideo;

  // 2-second delay before showing play button on preview (matches mobile behavior)
  useEffect(() => {
    if (!isPreviewOrder || !isOpen) { setPreviewReady(false); return; }
    setPreviewReady(false);
    const timer = setTimeout(() => setPreviewReady(true), 2000);
    return () => clearTimeout(timer);
  }, [isPreviewOrder, isOpen]);

  // DEMO RESULT viewed — for a free user this modal is the V1 preview result
  // (V2's PreviewReadyGate analog): once per open. The blocked actions below
  // (play/download/share/fullscreen) fire preview_cta_clicked; the PAYWALL
  // event fires only on the pricing page they get routed to.
  useEffect(() => {
    if (!isOpen || isProUser || !isPreviewOrder) return;
    trackVideoPreviewReady();
  }, [isOpen, isProUser, isPreviewOrder]);

  // Prevent screenshots/screen recording/fullscreen for free users (Desktop + Mobile)
  useEffect(() => {
    if (!isOpen || isProUser) return;

    // Disable right-click globally when modal is open for free users
    const handleContextMenu = (e: MouseEvent | TouchEvent) => {
      e.preventDefault();
      return false;
    };

    // Disable keyboard shortcuts for screenshots and fullscreen (Desktop)
    const handleKeyDown = (e: KeyboardEvent) => {
      // Prevent PrintScreen, Cmd+Shift+3/4 (Mac screenshot), Win+PrintScreen
      if (
        e.key === 'PrintScreen' ||
        (e.metaKey && e.shiftKey && (e.key === '3' || e.key === '4' || e.key === '5')) ||
        (e.metaKey && e.key === 'PrintScreen')
      ) {
        e.preventDefault();
        navigate('/subscription?context=screenshot');
        onClose();
        return false;
      }
      
      // Prevent fullscreen keyboard shortcuts (F, F11, Escape to exit won't trigger this)
      if (e.key === 'f' || e.key === 'F' || e.key === 'F11') {
        e.preventDefault();
        navigate('/subscription?context=fullscreen');
        onClose();
        return false;
      }
    };

    // Block fullscreen attempts (Desktop + Safari iOS)
    const handleFullscreenChange = () => {
      if (document.fullscreenElement || (document as any).webkitFullscreenElement) {
        // Exit fullscreen immediately
        if (document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        } else if ((document as any).webkitExitFullscreen) {
          (document as any).webkitExitFullscreen();
        }
        trackFullscreenBlocked();
        trackPaywallCtaClicked('fullscreen');
        navigate('/subscription?context=fullscreen');
        onClose();
      }
    };
    
    // Safari iOS specific: Handle video entering fullscreen
    const handleWebkitBeginFullscreen = () => {
      // Exit fullscreen and redirect to upgrade
      const video = videoRef.current;
      if (video && (video as any).webkitExitFullscreen) {
        (video as any).webkitExitFullscreen();
      }
      navigate('/subscription?context=fullscreen');
      onClose();
    };

    // Mobile: Prevent long-press context menu (for saving images/videos)
    // Only target media elements, not buttons or other UI elements
    const handleTouchStart = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      // Only track touch on media elements inside the media container
      if (
        target.closest('.free-user-media-container') &&
        (target.tagName === 'VIDEO' || target.tagName === 'IMG' || target.tagName === 'AUDIO' || target.closest('video') || target.closest('img'))
      ) {
        (window as any).__touchStartTime = Date.now();
        (window as any).__touchTarget = target;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      // Only prevent default on media elements after long press
      if ((window as any).__touchTarget && target.closest('.free-user-media-container')) {
        const touchDuration = Date.now() - ((window as any).__touchStartTime || 0);
        if (touchDuration > 500) {
          e.preventDefault();
        }
      }
      (window as any).__touchStartTime = 0;
      (window as any).__touchTarget = null;
    };

    // iOS Safari: Handle presentation mode change (fullscreen/pip)
    const handlePresentationModeChange = (e: Event) => {
      const video = e.target as HTMLVideoElement;
      const presentationMode = (video as any).webkitPresentationMode;
      if (presentationMode === 'fullscreen') {
        // Exit fullscreen and redirect
        if ((video as any).webkitSetPresentationMode) {
          (video as any).webkitSetPresentationMode('inline');
        }
        navigate('/subscription?context=fullscreen');
        onClose();
      }
    };

    // Intercept fullscreen request on the video element
    const videoElement = videoRef.current;
    if (videoElement) {
      // Override the native fullscreen request
      videoElement.requestFullscreen = function() {
        navigate('/subscription?context=fullscreen');
        onClose();
        return Promise.reject(new Error('Fullscreen blocked for free users'));
      };
      
      // Also handle webkit fullscreen (Safari/iOS)
      if ('webkitRequestFullscreen' in videoElement) {
        (videoElement as any).webkitRequestFullscreen = function() {
          navigate('/subscription?context=fullscreen');
          onClose();
        };
      }
      
      // iOS Safari: Handle webkitEnterFullscreen for video (native fullscreen button)
      if ('webkitEnterFullscreen' in videoElement) {
        (videoElement as any).webkitEnterFullscreen = function() {
          navigate('/subscription?context=fullscreen');
          onClose();
        };
      }
      
      // iOS Safari: Handle webkitSetPresentationMode
      if ('webkitSetPresentationMode' in videoElement) {
        const originalSetPresentationMode = (videoElement as any).webkitSetPresentationMode;
        (videoElement as any).webkitSetPresentationMode = function(mode: string) {
          if (mode === 'fullscreen') {
            navigate('/subscription?context=fullscreen');
            onClose();
            return;
          }
          return originalSetPresentationMode?.call(videoElement, mode);
        };
      }

      // Mobile: Add touch event listeners to video
      videoElement.addEventListener('touchstart', handleTouchStart, { passive: false });
      videoElement.addEventListener('touchend', handleTouchEnd, { passive: false });
      
      // Safari iOS: Listen for fullscreen events on video element
      videoElement.addEventListener('webkitbeginfullscreen', handleWebkitBeginFullscreen);
      videoElement.addEventListener('webkitfullscreenchange', handleFullscreenChange);
      videoElement.addEventListener('webkitpresentationmodechanged', handlePresentationModeChange);
      
      // iOS: Set attributes for inline playback
      videoElement.setAttribute('playsinline', 'true');
      videoElement.setAttribute('webkit-playsinline', 'true');
    }

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    
    // Mobile event listeners - use passive: true to not block scrolling/buttons
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });

    // Add CSS to prevent mobile interactions for free users (Safari iOS compatible)
    const styleId = 'vivideo-free-user-restrictions';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        /* Prevent text/image selection on mobile (Safari iOS compatible) */
        .free-user-media-container {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
          -webkit-touch-callout: none !important;
          -webkit-tap-highlight-color: transparent !important;
        }
        /* Prevent iOS Safari save image menu */
        .free-user-media-container img,
        .free-user-media-container video {
          -webkit-touch-callout: none !important;
          pointer-events: auto !important;
        }
        /* Hide PiP button but keep fullscreen visible */
        .free-user-media-container video::-webkit-media-controls-picture-in-picture-button {
          display: none !important;
        }
      `;
      document.head.appendChild(style);
    }

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
      
      // Clean up video event listeners
      if (videoElement) {
        videoElement.removeEventListener('touchstart', handleTouchStart);
        videoElement.removeEventListener('touchend', handleTouchEnd);
        videoElement.removeEventListener('webkitbeginfullscreen', handleWebkitBeginFullscreen);
        videoElement.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        videoElement.removeEventListener('webkitpresentationmodechanged', handlePresentationModeChange);
      }
      
      // Clean up global touch tracking
      (window as any).__touchStartTime = 0;
      (window as any).__touchTarget = null;
      
      // Remove injected styles
      const style = document.getElementById('vivideo-free-user-restrictions');
      if (style) {
        style.remove();
      }
    };
  }, [isOpen, isProUser, navigate, onClose]);

  // Allow preview orders that have previewImageUrl even without videoUrl/outputUrl
  if (!order || (!order.videoUrl && !order.outputUrl && !order.previewImageUrl)) return null;

  const fileType = getFileTypeFromOperation(order.operationType);
  // Use outputUrl (our S3) first, then videoUrl as fallback for preview/playback
  const previewUrl = order.outputUrl || order.videoUrl;
  const downloadUrl = order.outputUrl || order.videoUrl;
  
  // Handle fake play for preview orders
  const handlePreviewPlay = () => {
    if (isPlayingPreview) return;
    setIsPlayingPreview(true);
    setPreviewPlayProgress(0);
    
    // Animate progress bar over 2 seconds
    const startTime = Date.now();
    const duration = 2000; // 2 seconds
    
    const animateProgress = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min((elapsed / duration) * 100, 100);
      setPreviewPlayProgress(progress);
      
      if (progress < 100) {
        requestAnimationFrame(animateProgress);
      } else {
        // After 1 second, redirect to subscription
        setTimeout(() => {
          setIsPlayingPreview(false);
          setPreviewPlayProgress(0);
          trackPaywallCtaClicked('play');
          navigate('/subscription?context=upgrade_for_full_video');
          onClose();
        }, 200);
      }
    };
    
    requestAnimationFrame(animateProgress);
  };

  const handleDownload = async () => {
    if (!isProUser) {
      trackPaywallCtaClicked('download');
      navigate('/subscription?context=download');
      onClose();
      return;
    }
    
    if (isDownloading || !downloadUrl) return;
    setIsDownloading(true);

    const extension = fileType === 'video' ? 'mp4' : fileType === 'audio' ? 'mp3' : 'jpg';
    const filename = `${order.orderName || 'vivideo'}.${extension}`;

    // Append a cache-busting query param. The same URL is used by the <video>
    // preview element (loaded without CORS), which causes Chrome to cache the
    // response without CORS headers. A subsequent `fetch()` (which always runs
    // in CORS mode) is then served from that cache and fails with
    // `net::ERR_FAILED`. The extra query param forces a fresh network request
    // with proper CORS headers.
    const cacheBustedUrl = `${downloadUrl}${downloadUrl.includes('?') ? '&' : '?'}_dl=${Date.now()}`;

    try {
      const response = await fetch(cacheBustedUrl, {
        cache: 'no-store',
        mode: 'cors',
        credentials: 'omit',
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      trackVideoDownloaded(order.operationType);
    } catch (error) {
      console.error('Download via fetch failed, falling back to direct link:', error);
      // Fallback: open in a new tab so the user can still save the file via
      // right-click / the browser's built-in download. Cross-origin `download`
      // attribute is ignored by browsers, so this is the most reliable fallback.
      try {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        trackVideoDownloaded(order.operationType);
      } catch (fallbackError) {
        console.error('Download fallback failed:', fallbackError);
      }
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async () => {
    if (!isProUser) {
      trackPaywallCtaClicked('share');
      navigate('/subscription?context=share');
      onClose();
      return;
    }
    
    if (isSharing) return;
    setIsSharing(true);
    
    // Use downloadUrl (output_url from our S3) for sharing
    const shareData = {
      title: order.orderName || 'Vivideo Creation',
      text: `Check out my AI-generated ${fileType} created with Vivideo!`,
      url: downloadUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        // Track successful share — the OS sheet is the only share path here.
        trackVideoShared(order.operationType, {
          share_method: 'native',
          order_id: order.orderId,
          provider: order.videoProvider || undefined,
        });
      }
    } catch (err) {
      console.error('Error sharing:', err);
    } finally {
      setIsSharing(false);
    }
  };

  // Handle right-click on media to show upgrade prompt for free users
  const handleMediaContextMenu = (e: React.MouseEvent) => {
    if (!isProUser) {
      e.preventDefault();
      navigate('/subscription?context=download');
      onClose();
      return false;
    }
  };

  // HeyGen video edit (auto mode)
  const HEYGEN_DURATION_STEPS = [5, 10, 15, 20, 30, 45, 60, 90, 120, 180];
  const HEYGEN_DURATION_MARKERS = [
    { index: 0, label: '5s' },
    { index: 4, label: '30s' },
    { index: 6, label: '1m' },
    { index: 9, label: '3m' },
  ];
  const heygenEditCreditCost = calculateHeygenCredits(heygenEditDuration);
  const heygenStepIndex = HEYGEN_DURATION_STEPS.indexOf(heygenEditDuration);
  const heygenProgress = (heygenStepIndex / (HEYGEN_DURATION_STEPS.length - 1)) * 100;
  const formatDuration = (s: number) => { const m = Math.floor(s / 60); const sec = s % 60; if (m === 0) return `${sec}s`; if (sec === 0) return `${m}m`; return `${m}m ${sec}s`; };

  const handleHeygenEdit = async () => {
    if (!heygenEditInput.trim() || isEditingHeygen || !userId || !order) return;
    const videoUrl = order.outputUrl || order.videoUrl;
    if (!videoUrl) { setHeygenEditError('No video URL found'); return; }

    if (!hasEnoughCredits(heygenEditCreditCost)) {
      navigate('/subscription?context=insufficient_credits');
      onClose();
      return;
    }

    setIsEditingHeygen(true);
    setHeygenEditError(null);
    try {
      const { assetId } = await apiService.uploadHeygenAssetByUrl(userId, videoUrl);

      const orderData: Record<string, unknown> = {
        prompt: heygenEditInput.trim(),
        resolution: '720p',
        generate_audio: true,
        video_provider: 'heygen',
        duration: heygenEditDuration,
        files: [{ asset_id: assetId }],
      };

      await apiService.createOrder(userId, OPERATION_TYPES.TEXT_TO_VIDEO, orderData);
      decrementCredits(heygenEditCreditCost);
      setHeygenEditInput('');
      setHeygenEditStep('prompt');
      onClose();
      navigate('/files');
    } catch (err) {
      console.error('HeyGen edit error:', err);
      setHeygenEditError(t('errors.somethingWentWrong'));
    } finally {
      setIsEditingHeygen(false);
    }
  };

  // Video remix using Sora 2 API
  const handleRecreateVideo = async () => {
    if (!recreateInput.trim() || isRecreating || !userId || !order) return;
    
    // Debug: Log order data to identify video_id location
    console.log('Order data for remix:', JSON.stringify(order, null, 2));
    
    // Check if order has fal video_id for remix
    if (!order.falVideoId) {
      console.error('Remix error: No falVideoId found in order');
      setRecreateError(t('errors.remixNotAvailable') || 'Remix not available for this video');
      return;
    }
    
    // Check credits
    if (!hasEnoughCredits(remixCreditCost)) {
      navigate('/subscription?context=insufficient_credits');
      onClose();
      return;
    }
    
    setIsRecreating(true);
    setRecreateError(null);
    
    // Track remix started
    trackVideoRemixStarted(order.operationType);
    
    try {
      // Call video_remix API using Sora 2 remix
      await apiService.createOrder(userId, OPERATION_TYPES.VIDEO_REMIX, {
        fal_video_id: order.falVideoId,
        prompt: recreateInput.trim(),
        original_fal_model: order.falModel, // Pass original model for credit calculation
      });
      
      // Deduct credits
      decrementCredits(remixCreditCost);
      
      // Clear input and navigate to files page
      setRecreateInput('');
      onClose();
      navigate('/files?highlight=true');
    } catch (error) {
      console.error('Error re-creating video:', error);
      const errorStr = String(error).toLowerCase();
      
      if (errorStr.includes('402') || errorStr.includes('insufficient_credits') || errorStr.includes('insufficient credits')) {
        navigate('/subscription?context=insufficient_credits');
        onClose();
        return;
      }
      
      setRecreateError(t('errors.errorCreatingVideo') || 'Failed to create video');
    } finally {
      setIsRecreating(false);
    }
  };

  // Video extend (backend maps it to H3 Max extend)
  const handleExtendVideo = async () => {
    if (!extendInput.trim() || isExtending || !userId || !order) return;
    
    // Check if order has video URL for extend
    const videoUrl = order.outputUrl || order.videoUrl;
    if (!videoUrl) {
      console.error('Extend error: No video URL found in order');
      setExtendError(t('errors.errorCreatingVideo') || 'Video URL not available');
      return;
    }
    
    // Check credits
    if (!hasEnoughCredits(extendCreditCost)) {
      navigate('/subscription?context=insufficient_credits');
      onClose();
      return;
    }
    
    setIsExtending(true);
    setExtendError(null);
    
    try {
      // Call video_extend API (backend runs H3 Max extend-video)
      await apiService.createOrder(userId, OPERATION_TYPES.VIDEO_EXTEND, {
        video_url: videoUrl,
        prompt: extendInput.trim(),
      });
      
      // Deduct credits
      decrementCredits(extendCreditCost);
      
      // Clear input and navigate to files page
      setExtendInput('');
      onClose();
      navigate('/files?highlight=true');
    } catch (error) {
      console.error('Error extending video:', error);
      const errorStr = String(error).toLowerCase();
      
      if (errorStr.includes('402') || errorStr.includes('insufficient_credits') || errorStr.includes('insufficient credits')) {
        navigate('/subscription?context=insufficient_credits');
        onClose();
        return;
      }
      
      setExtendError(t('errors.errorCreatingVideo') || 'Failed to extend video');
    } finally {
      setIsExtending(false);
    }
  };

  // Video edit using Wan API
  const handleEditWanVideo = async () => {
    if (!editWanInput.trim() || isEditingWan || !userId || !order) return;
    
    // Check if order has video URL for edit
    const videoUrl = order.outputUrl || order.videoUrl;
    if (!videoUrl) {
      console.error('Edit error: No video URL found in order');
      setEditWanError(t('errors.errorCreatingVideo') || 'Video URL not available');
      return;
    }
    
    // Check credits
    if (!hasEnoughCredits(editWanCreditCost)) {
      navigate('/subscription?context=insufficient_credits');
      onClose();
      return;
    }
    
    setIsEditingWan(true);
    setEditWanError(null);
    
    try {
      // Call video_edit_wan API using Wan reference-to-video
      await apiService.createOrder(userId, OPERATION_TYPES.VIDEO_EDIT_WAN, {
        video_url: videoUrl,
        prompt: editWanInput.trim(),
        aspect_ratio: '16:9', // Default, could be extracted from original order
        original_duration: '5', // Default, could be extracted from original order
      });
      
      // Deduct credits
      decrementCredits(editWanCreditCost);
      
      // Clear input and navigate to files page
      setEditWanInput('');
      onClose();
      navigate('/files?highlight=true');
    } catch (error) {
      console.error('Error editing video:', error);
      const errorStr = String(error).toLowerCase();
      
      if (errorStr.includes('402') || errorStr.includes('insufficient_credits') || errorStr.includes('insufficient credits')) {
        navigate('/subscription?context=insufficient_credits');
        onClose();
        return;
      }
      
      setEditWanError(t('errors.errorCreatingVideo') || 'Failed to edit video');
    } finally {
      setIsEditingWan(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={order.orderName || 'Media Details'}
      size="lg"
    >
      <div className="space-y-3 pb-2">
        {/* Media Preview - Taller container for mobile, even taller for preview orders */}
        <div 
          className={cn(
            "relative rounded-xl overflow-hidden bg-black flex-shrink-0",
            isPreviewOrder 
              ? "aspect-[9/13] sm:aspect-[9/12] md:aspect-[16/10]"
              : "aspect-[9/11] sm:aspect-[9/10] md:aspect-video",
            !isProUser && "select-none free-user-media-container"
          )}
          onContextMenu={handleMediaContextMenu}
          // Mobile: Prevent long-press context menu
          onTouchStart={(e) => {
            if (!isProUser) {
              (e.currentTarget as any).__touchTimer = setTimeout(() => {
                e.preventDefault();
              }, 500);
            }
          }}
          onTouchEnd={(e) => {
            if (!isProUser && (e.currentTarget as any).__touchTimer) {
              clearTimeout((e.currentTarget as any).__touchTimer);
            }
          }}
          onTouchMove={(e) => {
            if (!isProUser && (e.currentTarget as any).__touchTimer) {
              clearTimeout((e.currentTarget as any).__touchTimer);
            }
          }}
        >
          {/* PRO overlay for free users (only for non-preview and non-free-video-preview orders) */}
          {!isProUser && !isPreviewOrder && (
            <div className="absolute inset-0 z-10 pointer-events-none">
              {/* Watermark - smaller */}
              <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm">
                <Lock className="w-3 h-3 text-amber-400" />
                <span className="text-[10px] font-semibold text-amber-400">PRO to Download</span>
              </div>
            </div>
          )}
          
          {/* Preview Order - Show image with play button */}
          {isPreviewOrder && order.previewImageUrl && (
            <div className="relative w-full h-full">
              <img 
                src={order.previewImageUrl} 
                alt={order.orderName} 
                className="w-full h-full object-cover"
                style={{ filter: 'blur(3px)' }}
              />
              {/* Loading spinner or play button overlay */}
              <div 
                className="absolute inset-0 flex items-center justify-center cursor-pointer"
                onClick={previewReady ? handlePreviewPlay : undefined}
              >
                <div className={cn(
                  "w-20 h-20 rounded-full flex items-center justify-center transition-all border-2 border-white",
                  !previewReady
                    ? "bg-primary/20 backdrop-blur-sm"
                    : isPlayingPreview 
                      ? "bg-primary/20 backdrop-blur-sm" 
                      : "bg-primary/30 backdrop-blur-sm hover:bg-primary/40 hover:scale-110"
                )}>
                  {!previewReady || isPlayingPreview ? (
                    <Loader2 className="w-10 h-10 text-primary animate-spin" />
                  ) : (
                    <Play className="w-10 h-10 text-white fill-primary ml-1" />
                  )}
                </div>
              </div>
              
              {/* Progress bar when playing */}
              {isPlayingPreview && (
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
                  <div 
                    className="h-full bg-primary transition-all duration-100"
                    style={{ width: `${previewPlayProgress}%` }}
                  />
                </div>
              )}
            </div>
          )}
          
          {/* Regular video/audio/image content (only for non-preview orders) */}
          {!isPreviewOrder && fileType === 'video' && (
            <video
              ref={videoRef}
              src={previewUrl}
              className={cn(
                "w-full h-full object-contain",
                !isProUser && "pointer-events-auto"
              )}
              controls
              autoPlay={false}
              controlsList={!isProUser ? "nodownload noplaybackrate" : undefined}
              disablePictureInPicture={!isProUser}
              playsInline
              // @ts-ignore - webkit specific attribute for older Safari
              webkit-playsinline="true"
              onContextMenu={handleMediaContextMenu}
              onDoubleClick={(e) => {
                if (!isProUser) {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate('/subscription?context=fullscreen');
                  onClose();
                }
              }}
            />
          )}
          
          {!isPreviewOrder && fileType === 'audio' && (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-accent-green/20 to-accent-green/5">
              <div className="w-16 h-16 rounded-full bg-accent-green/20 flex items-center justify-center mb-3">
                <Music className="w-8 h-8 text-accent-green" />
              </div>
              <audio
                src={previewUrl}
                controls
                className="w-full max-w-md px-4"
                controlsList={!isProUser ? "nodownload" : undefined}
                onContextMenu={handleMediaContextMenu}
              />
            </div>
          )}
          
          {!isPreviewOrder && fileType === 'image' && (
            <img
              src={previewUrl}
              alt={order.orderName}
              className={cn(
                "w-full h-full object-contain",
                !isProUser && "pointer-events-none"
              )}
              draggable={isProUser}
              onContextMenu={handleMediaContextMenu}
              // Mobile: Prevent long-press save
              onTouchStart={(e) => {
                if (!isProUser) {
                  e.preventDefault();
                }
              }}
              style={!isProUser ? {
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
              } : undefined}
            />
          )}
        </div>

        {/* Extend Video - Veo 3.1 orders (any veo3.1 id) and Gemini Omni Flash / H3 Max orders */}
        {!isPreviewOrder && fileType === 'video' && isVeo31Video && (
          <div className="space-y-2">
            <div>
              <Input
                placeholder={t('mediaDetail.extendVideoPlaceholder')}
                value={extendInput}
                onChange={(e) => {
                  setExtendInput(e.target.value);
                  if (extendError) setExtendError(null);
                }}
                className="w-full"
              />
              <p className="mt-1.5 text-xs text-text-tertiary">
                {t('mediaDetail.extendVideoDescription')}
              </p>
            </div>
            
            {/* Error message */}
            {extendError && (
              <p className="text-xs text-status-error">{extendError}</p>
            )}
            
            {/* Extend Video Button */}
            <button
              onClick={handleExtendVideo}
              disabled={!extendInput.trim() || isExtending}
              className={cn(
                "w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl",
                "bg-primary hover:bg-primary/90 text-white font-medium",
                "transition-all disabled:opacity-50 disabled:cursor-not-allowed",
                "relative"
              )}
            >
              {isExtending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('mediaDetail.extending')}</span>
                </>
              ) : (
                <>
                  <ChevronsRight className="w-4 h-4" />
                  <span>{t('mediaDetail.extendVideo')}</span>
                  {/* Credit badge */}
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-primary to-accent rounded">
                    {extendCreditCost} {t('account.credits').toLowerCase()}
                  </span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Edit Video (Sora 2 Remix / Grok Edit) */}
        {!isPreviewOrder && fileType === 'video' && 
         order.operationType !== 'multiple_images' && 
         supportsEdit && (
          <div className="space-y-2">
            <div>
              <Input
                placeholder={t('mediaDetail.tellWhatToChange')}
                value={recreateInput}
                onChange={(e) => {
                  setRecreateInput(e.target.value);
                  if (recreateError) setRecreateError(null);
                }}
                className="w-full"
              />
              <p className="mt-1.5 text-xs text-text-tertiary">
                {t('mediaDetail.recreateDescription')}
              </p>
            </div>
            
            {/* Error message */}
            {recreateError && (
              <p className="text-xs text-status-error">{recreateError}</p>
            )}
            
            {/* Re-create Video Button */}
            <button
              onClick={handleRecreateVideo}
              disabled={!recreateInput.trim() || isRecreating}
              className={cn(
                "w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl",
                "bg-primary hover:bg-primary/90 text-white font-medium",
                "transition-all disabled:opacity-50 disabled:cursor-not-allowed",
                "relative"
              )}
            >
              {isRecreating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('mediaDetail.recreating')}</span>
                </>
              ) : (
                <>
                  <Pencil className="w-4 h-4" />
                  <span>{t('mediaDetail.editVideo')}</span>
                  {/* Credit badge */}
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-primary to-accent rounded">
                    {remixCreditCost} {t('account.credits').toLowerCase()}
                  </span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Edit Video (Wan) - for Wan videos */}
        {!isPreviewOrder && fileType === 'video' && isWanVideo && (
          <div className="space-y-2">
            <div>
              <Input
                placeholder={t('mediaDetail.editVideoPlaceholder')}
                value={editWanInput}
                onChange={(e) => {
                  setEditWanInput(e.target.value);
                  if (editWanError) setEditWanError(null);
                }}
                className="w-full"
              />
              <p className="mt-1.5 text-xs text-text-tertiary">
                {t('mediaDetail.editVideoDescription')}
              </p>
            </div>
            
            {/* Error message */}
            {editWanError && (
              <p className="text-xs text-status-error">{editWanError}</p>
            )}
            
            {/* Edit Video Button */}
            <button
              onClick={handleEditWanVideo}
              disabled={!editWanInput.trim() || isEditingWan}
              className={cn(
                "w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl",
                "bg-primary hover:bg-primary/90 text-white font-medium",
                "transition-all disabled:opacity-50 disabled:cursor-not-allowed",
                "relative"
              )}
            >
              {isEditingWan ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('mediaDetail.editing')}</span>
                </>
              ) : (
                <>
                  <Pencil className="w-4 h-4" />
                  <span>{t('mediaDetail.editVideo')}</span>
                  {/* Credit badge */}
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs font-bold bg-gradient-to-r from-primary to-accent rounded">
                    {editWanCreditCost} {t('account.credits').toLowerCase()}
                  </span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Edit Video (HeyGen/Auto mode) */}
        {!isPreviewOrder && fileType === 'video' && isHeygenVideo && order.orderStatus === 'completed' && (
          <div className="space-y-2">
            <div>
              <Input
                placeholder={t('mediaDetail.editVideoPlaceholder')}
                value={heygenEditInput}
                onChange={(e) => {
                  setHeygenEditInput(e.target.value);
                  if (heygenEditError) setHeygenEditError(null);
                }}
                className="w-full"
              />
              <p className="mt-1.5 text-xs text-text-tertiary">
                {t('mediaDetail.editVideoDescription')}
              </p>
            </div>
            {heygenEditError && (
              <p className="text-xs text-status-error">{heygenEditError}</p>
            )}
            <button
              onClick={() => { if (heygenEditInput.trim()) setHeygenEditStep('duration'); }}
              disabled={!heygenEditInput.trim() || isEditingHeygen}
              className={cn(
                "w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl",
                "bg-primary hover:bg-primary/90 text-white font-medium",
                "transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              )}
            >
              {isEditingHeygen ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('mediaDetail.editing')}</span>
                </>
              ) : (
                <>
                  <Pencil className="w-4 h-4" />
                  <span>{t('mediaDetail.editVideo')}</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* HeyGen Edit Duration Selector Modal */}
        {heygenEditStep === 'duration' && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center animate-fade-in">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setHeygenEditStep('prompt')} />
            <div className="relative z-10 w-full max-w-sm mx-4 rounded-2xl bg-surface border border-glass-border shadow-2xl p-6">
              <h3 className="text-lg font-bold text-text-primary mb-1 text-center">{t('home.selectDuration')}</h3>
              <p className="text-sm text-text-secondary text-center mb-6">{t('home.durationHint')}</p>

              <div className="text-center py-4">
                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20">
                  <Clock className="w-6 h-6 text-primary" />
                  <span className="text-4xl font-bold text-text-primary tabular-nums">
                    {formatDuration(heygenEditDuration)}
                  </span>
                </div>
              </div>

              <div className="px-1 pt-2 pb-6">
                <div className="relative">
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-2 rounded-full bg-surface-light" />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 h-2 rounded-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-150"
                    style={{ width: `${heygenProgress}%` }}
                  />
                  <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-0">
                    {HEYGEN_DURATION_STEPS.map((_, idx) => (
                      <div key={idx} className={cn("w-1 h-1 rounded-full transition-colors", idx <= heygenStepIndex ? "bg-white/60" : "bg-text-tertiary/30")} />
                    ))}
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={HEYGEN_DURATION_STEPS.length - 1}
                    step={1}
                    value={heygenStepIndex}
                    onChange={(e) => setHeygenEditDuration(HEYGEN_DURATION_STEPS[parseInt(e.target.value, 10)])}
                    className="relative w-full h-6 appearance-none bg-transparent cursor-pointer z-10
                      [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-primary/30 [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110 [&::-webkit-slider-thumb]:active:scale-95
                      [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-lg [&::-moz-range-thumb]:shadow-primary/30 [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary [&::-moz-range-thumb]:transition-transform [&::-moz-range-thumb]:hover:scale-110"
                  />
                </div>
                <div className="relative mt-3 h-4">
                  {HEYGEN_DURATION_MARKERS.map(({ index, label }) => (
                    <span
                      key={index}
                      className={cn("absolute text-xs font-medium -translate-x-1/2 transition-colors", index <= heygenStepIndex ? "text-primary" : "text-text-tertiary")}
                      style={{ left: `${(index / (HEYGEN_DURATION_STEPS.length - 1)) * 100}%` }}
                    >
                      {label}
                    </span>
                  ))}
                </div>
              </div>

              <Button
                size="lg"
                className="w-full relative"
                onClick={handleHeygenEdit}
                isLoading={isEditingHeygen}
              >
                {t('home.confirmAndCreate')}
                <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 text-xs font-bold bg-white/10 rounded">
                  {heygenEditCreditCost} {t('account.credits').toLowerCase()}
                </span>
              </Button>
            </div>
          </div>
        )}

        {/* Preview Order - No extra UI needed, just the preview image with play */}
        {isPreviewOrder ? null : (
          <>
            {/* Upgrade Banner for Free Users */}
            {!isProUser && (
              <button
                onClick={() => {
                  // trackUpgradeBannerClicked records the click; the pricing
                  // page fires the paywall impression on arrival (this context).
                  trackUpgradeBannerClicked('media_detail_modal');
                  navigate('/subscription?context=upgrade_banner');
                  onClose();
                }}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 hover:border-amber-500/50 transition-all group"
              >
                <Sparkles className="w-4 h-4 text-amber-500 group-hover:scale-110 transition-transform" />
                <span className="text-sm font-medium text-amber-500">
                  {t('subscription.upgradeForLongerVideos')}
                </span>
              </button>
            )}

            {/* Actions - Smaller buttons */}
            <div className="flex gap-2">
              {/* Download */}
              <ActionButton
                icon={Download}
                label={isDownloading ? 'Saving...' : 'Download'}
                onClick={handleDownload}
                isLoading={isDownloading}
                showProBadge={!isProUser}
              />
              
              {/* Share */}
              <ActionButton
                icon={Share2}
                label={isSharing ? 'Sharing...' : 'Share'}
                onClick={handleShare}
                isLoading={isSharing}
                showProBadge={!isProUser}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// Action button component with PRO badge support
interface ActionButtonProps {
  icon: React.ElementType;
  label: string;
  onClick: () => void;
  isLoading?: boolean;
  showProBadge?: boolean;
  highlight?: boolean;
}

function ActionButton({ 
  icon: Icon, 
  label, 
  onClick, 
  isLoading = false, 
  showProBadge = false,
  highlight = false 
}: ActionButtonProps) {
  return (
    <button
      onClick={onClick}
      onTouchEnd={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={isLoading}
      className={cn(
        "flex-1 flex flex-col items-center gap-1 py-2 rounded-lg transition-all touch-manipulation",
        "bg-surface-light hover:bg-surface-light/80 border border-glass-border hover:border-primary/30 active:bg-surface-light",
        isLoading && "opacity-50 cursor-not-allowed",
        highlight && "border-accent-green text-accent-green"
      )}
      style={{ WebkitTapHighlightColor: 'transparent' }}
    >
      <div className="relative">
        <Icon className={cn(
          "w-5 h-5",
          highlight ? "text-accent-green" : "text-text-primary"
        )} />
        {showProBadge && (
          <span className="absolute -top-1.5 -right-4 px-1 py-0.5 text-[7px] font-bold text-white rounded bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg flex items-center gap-0.5">
            <Crown className="w-2 h-2" />
            PRO
          </span>
        )}
      </div>
      <span className={cn(
        "text-[10px] font-medium",
        highlight ? "text-accent-green" : "text-text-secondary"
      )}>
        {label}
      </span>
    </button>
  );
}
